import {
  Clock,
  Trash2,
  Reply,
  FileText,
  Grip,
  AlertCircle,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { scheduledMessageStatusConfig } from "../../utils/helpers";

const ScheduledMessagesList = ({ scheduledMessages, onCancel }) => {
  const [activeTab, setActiveTab] = useState("ALL");

  const tabs = [
    {
      id: "ALL",
      label: "Todas",
      icon: Grip,
      count: scheduledMessages.length,
    },
    {
      id: "PENDING",
      label: "Pendentes",
      icon: Clock,
      count: scheduledMessages.filter((msg) => msg.status === "PENDING").length,
    },
    {
      id: "SENT",
      label: "Enviadas",
      icon: CheckCircle,
      count: scheduledMessages.filter((msg) => msg.status === "SENT").length,
    },
    {
      id: "FAILED",
      label: "Falhas",
      icon: AlertCircle,
      count: scheduledMessages.filter((msg) => msg.status === "FAILED").length,
    },
    {
      id: "CANCELED",
      label: "Canceladas",
      icon: XCircle,
      count: scheduledMessages.filter((msg) => msg.status === "CANCELED")
        .length,
    },
  ];

  const filteredMessages =
    activeTab === "ALL"
      ? scheduledMessages
      : scheduledMessages.filter((msg) => msg.status === activeTab);

  return (
    <div>
      {/* Abas */}
      <div className="flex border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
        {tabs.map((tab) => {
          const IconComponent = tab.icon;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "border-b-2 border-blue-500 bg-white text-blue-600 dark:bg-gray-800 dark:text-blue-400"
                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <IconComponent className="h-4 w-4" />
                <span>{tab.label}</span>
                {tab.count > 0 && (
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs ${
                      activeTab === tab.id
                        ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                        : "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Lista de mensagens */}
      {filteredMessages.length === 0 ? (
        <p className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
          Nenhuma mensagem{" "}
          {activeTab === "ALL"
            ? "agendada"
            : scheduledMessageStatusConfig[activeTab]?.label.toLowerCase()}
          .
        </p>
      ) : (
        <div className="max-h-64 overflow-y-auto">
          {filteredMessages.map((msg) => {
            const date = new Date(msg.scheduleAt).toLocaleString("pt-BR", {
              timeZone: "America/Sao_Paulo",
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            });

            const status = scheduledMessageStatusConfig[msg.status];

            return (
              <div
                key={msg.id}
                className="flex items-center justify-between border-b border-gray-100 px-2 py-3 text-sm transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
              >
                {/* Conteúdo */}
                <div className="flex min-w-0 items-start space-x-3">
                  <div className="mt-1 text-blue-500 dark:text-blue-400">
                    <Clock className="h-4 w-4" />
                  </div>

                  <div className="min-w-0">
                    <p className="truncate font-medium text-gray-800 dark:text-gray-200">
                      Agendada para {date}
                    </p>

                    <div className="mt-0.5 space-y-1 text-xs text-gray-500 dark:text-gray-400">
                      {/* Mensagem citada */}
                      {msg.quoteMessageId && (
                        <div className="flex items-center space-x-2 rounded border-l-2 border-blue-400 bg-blue-50 px-2 py-1 dark:border-blue-500 dark:bg-blue-900/20">
                          <Reply className="h-3 w-3 shrink-0 text-blue-500 dark:text-blue-400" />
                          <span className="truncate italic">
                            Respondendo a uma mensagem
                          </span>
                        </div>
                      )}

                      {/* Texto */}
                      {msg.text && (
                        <p className="truncate text-gray-600 dark:text-gray-300">
                          {msg.text}
                        </p>
                      )}

                      {/* Anexo */}
                      {msg.attachmentUrl && (
                        <a
                          href={`${process.env.REACT_APP_URL}${msg.attachmentUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-2 rounded bg-gray-100 px-2 py-1 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          <FileText className="h-3 w-3 shrink-0" />
                          <span className="truncate">Anexo</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Ações */}
                <div className="flex items-center space-x-2">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${status.badge}`}
                  >
                    {status.label}
                  </span>

                  <button
                    onClick={() => onCancel?.(msg.jobId)}
                    disabled={msg.status !== "PENDING"}
                    title="Cancelar Envio"
                    className="flex items-center rounded bg-red-50 p-1 text-red-600 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ScheduledMessagesList;
